import React, { createContext, Dispatch, PropsWithChildren, useContext, useEffect, useReducer, useRef } from 'react';
import { getRecalls, IRecall } from '../API/GoMoto';
import { useAppointmentStateUnsafe } from './Appointment/appointment';
import { useKioskState } from './Kiosk/kiosk';
import { ActionMap } from '../API/GoMoto.types';

export enum RecallsDispatchTypes {
    SetLoading = 'SET_LOADING',
    SetRecalls = 'SET_RECALLS',
    ClearRecalls = 'CLEAR_RECALLS'
}

type RecallsPayload = {
    [RecallsDispatchTypes.ClearRecalls]: never;
    [RecallsDispatchTypes.SetLoading]: never;
    [RecallsDispatchTypes.SetRecalls]: IRecall[];
};

type RecallsActions = ActionMap<RecallsPayload>[keyof ActionMap<RecallsPayload>];

type RecallsStateType = {
    loading: boolean;
    recalls: IRecall[];
};

const initialState: RecallsStateType = {
    loading: false,
    recalls: [],
};

const recallsReducer = (state: RecallsStateType, action: RecallsActions) => {
    switch (action.type) {
        case RecallsDispatchTypes.SetLoading:
            return { ...state,
                loading: true };
        case RecallsDispatchTypes.ClearRecalls:
            return { ...state,
                loading: false,
                recalls: [] };
        case RecallsDispatchTypes.SetRecalls:
            return { ...state,
                loading: false,
                recalls: action.payload };
        default:
            return state;
    }
};

const RecallsStateContext = createContext<RecallsStateType>(initialState);
const RecallsDispatchContext = createContext<Dispatch<RecallsActions>>(() => null);

interface IRecallsProviderProps {
    suppressRecallCalls: boolean;
}

export const RecallsProvider: React.FC<PropsWithChildren<IRecallsProviderProps>> = (props) => {
    const [state, dispatch] = useReducer(recallsReducer, initialState);
    const appointment = useAppointmentStateUnsafe();
    const {
        key,
        dealer: { recallsActive },
    } = useKioskState();

    const vin = appointment?.vin ?? '';
    const apptKey = appointment?.key ?? '';
    const isMounted = useRef<boolean>(true);

    useEffect(() => {
        isMounted.current = true;
        dispatch({ type: RecallsDispatchTypes.ClearRecalls });

        if (!props.suppressRecallCalls && recallsActive && vin.length > 0) {
            dispatch({ type: RecallsDispatchTypes.SetLoading });

            getRecalls(key, apptKey, vin)
                .then((recalls: IRecall[]) => {
                    isMounted.current && dispatch({ type: RecallsDispatchTypes.SetRecalls,
                        payload: recalls });
                })
                .catch(() => {
                    isMounted.current && dispatch({ type: RecallsDispatchTypes.SetRecalls,
                        payload: [] });
                });
        }

        return () => {
            dispatch({ type: RecallsDispatchTypes.ClearRecalls });
            isMounted.current = false;
        };
    }, [recallsActive, key, vin, apptKey, props.suppressRecallCalls]);

    return (
        <RecallsStateContext.Provider value={state}>
            <RecallsDispatchContext.Provider value={dispatch}>{props.children}</RecallsDispatchContext.Provider>
        </RecallsStateContext.Provider>
    );
};

export const useRecallsState = () => {
    const context = useContext(RecallsStateContext);
    if (!context) {
        throw new Error('No Recalls Context');
    }

    return context;
};

export const useRecallsDispatch = () => {
    const context = useContext(RecallsDispatchContext);
    if (context === undefined) {
        throw new Error('No Recalls Dispatch Context');
    }

    return context;
};
